/**
 * Appointments and reminders due now: overdue first, then today. On the
 * practice dashboard a Mine / Team toggle; on a client's, one list. Each row
 * can be completed inline.
 */
import { CalendarCheck, Check, Loader2 } from "lucide-react";
import { useState, type FC } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

import { ForeignNationalsAPI } from "../../../../api/foreignNationals";
import { invalidateClient } from "../../../clientProfile/lib/clientInvalidation";
import { dueLabel, urgencyClass } from "../../format";
import type { DashboardMode, ReminderRow, TodaySplit } from "../../types";
import { EntityLink } from "../EntityLink";
import { CardRow, DashboardCard } from "./DashboardCard";

type Props = {
  mode: DashboardMode;
  today: TodaySplit | undefined;
  /** Due later in the window, shown after the due rows (collapsed count). */
  upcoming?: ReminderRow[];
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onViewAll?: () => void;
  /** Called after a reminder is completed so the summary can refresh. */
  onChanged?: () => void;
  span?: "full";
};

type View = "mine" | "team";

export const TodayCard: FC<Props> = ({
  mode,
  today,
  upcoming = [],
  isLoading,
  error,
  onRetry,
  onViewAll,
  onChanged,
  span
}) => {
  const [view, setView] = useState<View>("mine");
  const [busy, setBusy] = useState<string | null>(null);

  const rows: ReminderRow[] =
    mode === "practice" ? today?.[view] ?? [] : today?.team ?? [];
  const showToggle = mode === "practice";
  const upcomingCount = upcoming.length;

  const complete = async (row: ReminderRow) => {
    setBusy(row.reminder_id);
    try {
      await ForeignNationalsAPI.updateReminder(
        row.foreign_national_id,
        row.reminder_id,
        { status: "COMPLETED" }
      );
      toast.success("Marked complete");
      invalidateClient(row.foreign_national_id, ["reminders"]);
      onChanged?.();
    } catch (e) {
      toast.error("Couldn't complete the reminder", {
        description: e instanceof Error ? e.message : undefined
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <DashboardCard
      icon={CalendarCheck}
      title="Today"
      count={rows.length}
      controls={
        showToggle ? (
          <ToggleGroup
            type="single"
            size="sm"
            value={view}
            onValueChange={(v) => v && setView(v as View)}
            className="mr-1"
            data-testid="today-toggle"
          >
            <ToggleGroupItem
              value="mine"
              className="h-6 rounded-sm px-2 text-xs"
            >
              Mine
            </ToggleGroupItem>
            <ToggleGroupItem
              value="team"
              className="h-6 rounded-sm px-2 text-xs"
            >
              Team
            </ToggleGroupItem>
          </ToggleGroup>
        ) : null
      }
      onViewAll={onViewAll}
      isLoading={isLoading && !today}
      error={error}
      onRetry={onRetry}
      isEmpty={rows.length === 0}
      emptyTitle={
        mode === "practice" && view === "mine"
          ? "Nothing Due for You Today"
          : "Nothing Due Today"
      }
      emptyDescription={
        upcomingCount
          ? `${upcomingCount} coming up later in the window.`
          : undefined
      }
      footer={
        upcomingCount ? (
          <div className="border-t border-border px-3 py-1.5 text-xs text-muted-foreground">
            {upcomingCount} more coming up later
          </div>
        ) : null
      }
      span={span}
      testId="today-card"
    >
      {rows.map((row) => {
        const isBusy = busy === row.reminder_id;
        const clientLabel =
          mode === "practice" && row.client ? row.client : null;
        return (
          <CardRow
            key={row.reminder_id}
            leading={
              <Button
                variant="outline"
                size="icon-xs"
                className="size-5 rounded-sm text-muted-foreground hover:text-foreground"
                aria-label="Mark complete"
                disabled={isBusy}
                onClick={(e) => {
                  e.stopPropagation();
                  void complete(row);
                }}
                data-testid="today-complete"
              >
                {isBusy ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  <Check className="size-3" />
                )}
              </Button>
            }
            primary={
              <EntityLink path={row.path} refMarkdown={row.ref}>
                {row.title}
              </EntityLink>
            }
            secondary={[
              row.kind === "appointment" ? "Appointment" : null,
              clientLabel,
              row.assignees.length ? row.assignees.join(", ") : null
            ]
              .filter(Boolean)
              .join(" · ")}
            trailing={
              <span
                className={cn(
                  "text-xs tabular-nums",
                  urgencyClass(row.days_left, row.overdue)
                )}
              >
                {dueLabel(row.due, { allDay: row.all_day })}
              </span>
            }
            testId="today-row"
          />
        );
      })}
    </DashboardCard>
  );
};
